import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { TimelineEvent } from "../../types/metrics";
import { callMetricsService } from "../../services/callMetricsService";

interface ResponseLatencyBreakdownProps {
  timeline: TimelineEvent[];
}

export const ResponseLatencyBreakdown: React.FC<
  ResponseLatencyBreakdownProps
> = ({ timeline }) => {
  // Group timeline events into response cycles (STT → LLM → TTS)
  const responseCycles = useMemo(() => {
    const cycles: Array<{
      id: number;
      stt: number;
      llm: number;
      tts: number;
      total: number;
      timestamp: number;
      llm_tokens?: {
        prompt: number;
        completion: number;
        total: number;
        cached: number;
        speed: number;
      };
    }> = [];

    let cycleId = 0;
    let currentCycle: any = { id: cycleId, stt: 0, llm: 0, tts: 0 };

    timeline.forEach((event, index) => {
      switch (event.type) {
        case "STT":
          // Start a new cycle with STT
          if (currentCycle.stt > 0) {
            // Push previous cycle if it has STT
            cycles.push({
              ...currentCycle,
              total: currentCycle.stt + currentCycle.llm + currentCycle.tts,
              timestamp: event.start,
            });
            cycleId++;
            currentCycle = { id: cycleId, stt: 0, llm: 0, tts: 0 };
          }
          currentCycle.stt = event.duration;
          currentCycle.timestamp = event.start;
          break;

        case "LLM":
          currentCycle.llm = event.duration;
          // Capture token information
          if (event.total_tokens) {
            currentCycle.llm_tokens = {
              prompt: event.prompt_tokens || 0,
              completion: event.completion_tokens || 0,
              total: event.total_tokens || 0,
              cached: event.cached_tokens || 0,
              speed: event.tokens_per_second || 0,
            };
          }
          break;

        case "TTS":
          currentCycle.tts = event.duration;
          // If this is the last event or next event is STT, complete the cycle
          if (index === timeline.length - 1 || timeline[index + 1]?.type === "STT") {
            cycles.push({
              ...currentCycle,
              total: currentCycle.stt + currentCycle.llm + currentCycle.tts,
              timestamp: currentCycle.timestamp,
            });
            cycleId++;
            currentCycle = { id: cycleId, stt: 0, llm: 0, tts: 0 };
          }
          break;
      }
    });

    // Limit to last 10 cycles for better visualization
    return cycles.slice(-10);
  }, [timeline]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[250px]">
          <p className="font-medium mb-2 border-b pb-2">
            Response Cycle #{data.id + 1}
          </p>

          {/* Latency breakdown */}
          <div className="space-y-1 mb-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-foreground">STT:</span>
              <span className="font-mono font-medium">
                {callMetricsService.formatDuration(data.stt)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-foreground">LLM:</span>
              <span className="font-mono font-medium">
                {callMetricsService.formatDuration(data.llm)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-good">TTS:</span>
              <span className="font-mono font-medium">
                {callMetricsService.formatDuration(data.tts)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 pt-2 border-t">
              <span className="font-semibold">Total:</span>
              <span className="font-mono font-bold">
                {callMetricsService.formatDuration(data.total)}
              </span>
            </div>
          </div>

          {/* LLM Token Details */}
          {data.llm_tokens && (
            <div className="border-t pt-2 mt-2">
              <p className="text-xs font-semibold text-foreground mb-1">
                LLM Tokens:
              </p>
              <div className="space-y-0.5 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Input:</span>
                  <span className="font-mono">
                    {data.llm_tokens.prompt.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Output:</span>
                  <span className="font-mono">
                    {data.llm_tokens.completion.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Total:</span>
                  <span className="font-mono font-semibold">
                    {data.llm_tokens.total.toLocaleString()}
                  </span>
                </div>
                {data.llm_tokens.cached > 0 && (
                  <div className="flex items-center justify-between gap-3 text-good">
                    <span>Cached:</span>
                    <span className="font-mono">
                      {data.llm_tokens.cached.toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 border-t pt-1 mt-1">
                  <span className="text-muted-foreground">Speed:</span>
                  <span className="font-mono">
                    {data.llm_tokens.speed.toFixed(1)} tok/s
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  if (!responseCycles || responseCycles.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Response Latency Breakdown</CardTitle>
          <CardDescription>No response cycle data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Response Latency Breakdown</CardTitle>
        <CardDescription>
          Time breakdown of response cycles (STT → LLM → TTS) - showing last 10
          cycles
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={responseCycles}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="id"
              tickFormatter={(value) => `#${value + 1}`}
              label={{
                value: "Response Cycle",
                position: "insideBottom",
                offset: -5,
              }}
            />
            <YAxis
              tickFormatter={(value) => callMetricsService.formatDuration(value)}
              label={{
                value: "Duration",
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{
                paddingTop: "20px",
              }}
            />
            <Bar
              dataKey="stt"
              stackId="a"
              fill={callMetricsService.getMetricColor("stt")}
              name="STT"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="llm"
              stackId="a"
              fill={callMetricsService.getMetricColor("llm")}
              name="LLM"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="tts"
              stackId="a"
              fill={callMetricsService.getMetricColor("tts")}
              name="TTS"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>

        {/* Summary Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div className="p-3 bg-muted rounded-lg">
            <div className="text-muted-foreground">Avg STT</div>
            <div className="text-xl font-bold text-foreground">
              {callMetricsService.formatDuration(
                responseCycles.reduce((sum, c) => sum + c.stt, 0) /
                  responseCycles.length
              )}
            </div>
          </div>
          <div className="p-3 bg-muted rounded-lg">
            <div className="text-muted-foreground">Avg LLM</div>
            <div className="text-xl font-bold text-foreground">
              {callMetricsService.formatDuration(
                responseCycles.reduce((sum, c) => sum + c.llm, 0) /
                  responseCycles.length
              )}
            </div>
          </div>
          <div className="p-3 bg-muted rounded-lg">
            <div className="text-muted-foreground">Avg TTS</div>
            <div className="text-xl font-bold text-good">
              {callMetricsService.formatDuration(
                responseCycles.reduce((sum, c) => sum + c.tts, 0) /
                  responseCycles.length
              )}
            </div>
          </div>
          <div className="p-3 bg-muted rounded-lg">
            <div className="text-muted-foreground">Avg Total</div>
            <div className="text-xl font-bold text-danger">
              {callMetricsService.formatDuration(
                responseCycles.reduce((sum, c) => sum + c.total, 0) /
                  responseCycles.length
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
